import { rankItem } from "@tanstack/match-sorter-utils"
import { FilterFn } from "@tanstack/react-table"

export const singleEnum: FilterFn<string> = (row, id, value?: string) => {
  if (!value) return true
  const rowValue = String(row.getValue(id))
  return rowValue === value
}

export const multiEnum: FilterFn<string> = (row, id, value?: string[]) => {
  if (!value?.length) return true
  const values = value.map((v) => String(v))
  const rowValue = String(row.getValue(id))
  return values.includes(rowValue)
}

export const boolean: FilterFn<string> = (row, id, value?: boolean) => {
  if (value === undefined) return true
  const rowValue = Boolean(row.getValue(id))
  return rowValue === value
}

/**
 * From https://tanstack.com/table/v8/docs/framework/react/examples/filters
 */
export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value)

  // Store the itemRank info
  addMeta({ itemRank })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

/**
 * From https://github.com/TanStack/table/blob/main/packages/table-core/src/filterFns.ts
 * but with it's `autoRemove` method modified to handle our "missing" boolean value in the third position of the tuple
 */
export const inNumberRange: FilterFn<any> = (
  row,
  columnId: string,
  filterValue: [number, number],
) => {
  const [min, max] = filterValue

  const rowValue = row.getValue<number>(columnId)
  return rowValue >= min && rowValue <= max
}

inNumberRange.resolveFilterValue = (val: [any, any]) => {
  const [unsafeMin, unsafeMax] = val

  const parsedMin =
    typeof unsafeMin !== "number" ? parseFloat(unsafeMin as string) : unsafeMin
  const parsedMax =
    typeof unsafeMax !== "number" ? parseFloat(unsafeMax as string) : unsafeMax

  let min = unsafeMin === null || Number.isNaN(parsedMin) ? -Infinity : parsedMin
  let max = unsafeMax === null || Number.isNaN(parsedMax) ? Infinity : parsedMax

  if (min > max) {
    const temp = min
    min = max
    max = temp
  }

  return [min, max] as const
}

inNumberRange.autoRemove = (val: any) =>
  testFalsey(val) || (testFalsey(val[0]) && testFalsey(val[1]) && testFalsey(val[2]))

function testFalsey(val: any) {
  return val === undefined || val === null || val === ""
}

export const inDateRange: FilterFn<any> = (
  row,
  columnId: string,
  filterValue: { start?: Date; end?: Date },
) => {
  const cellValue = row.getValue(columnId)

  // Return true if no filter value is set
  if (!filterValue || (!filterValue.start && !filterValue.end)) {
    return true
  }

  // Convert cell value to Date object if it isn't already
  let date: Date
  if (cellValue instanceof Date) {
    date = cellValue
  } else if (typeof cellValue === "string") {
    date = new Date(cellValue)
  } else if (typeof cellValue === "number") {
    date = new Date(cellValue)
  } else {
    return false // Invalid date value
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return false
  }

  const { start, end } = filterValue

  // Convert filter values to Date objects
  const startDate = start ? new Date(start) : null
  const endDate = end ? new Date(end) : null

  // Check start date constraint
  if (startDate && !isNaN(startDate.getTime())) {
    if (date < startDate) return false
  }

  // Check end date constraint
  if (endDate && !isNaN(endDate.getTime())) {
    if (date > endDate) return false
  }

  return true
}
